import {
	ChevronRightIcon,
	EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import {
	Activity,
	Award,
	Bell,
	Bookmark,
	Feather,
	type LucideIcon,
	Thermometer,
	Upload,
} from "lucide-react";
import type { CSSProperties } from "react";
import type { JourneyTask } from "../../../types/journey";
import styles from "./TaskCard.module.css";

export interface TaskCardProps {
	task: JourneyTask;
	view: "list" | "grid";
	onAskEve: (task: JourneyTask) => void;
	onChevronClick: (task: JourneyTask) => void;
	askEveLabel: string;
	progressLabel?: (count: number, total: number, unit: string) => string;
}

const ACTION_ICON: Record<string, LucideIcon> = {
	fill: Feather,
	upload: Upload,
	book: Bookmark,
	"review-sign": Award,
	test: Thermometer,
	notify: Bell,
	monitor: Activity,
};

export function TaskCard({
	task,
	view,
	onAskEve,
	onChevronClick,
	askEveLabel,
	progressLabel,
}: TaskCardProps) {
	const ActionIcon = ACTION_ICON[task.iconName ?? "fill"] ?? Feather;
	const showProgress =
		task.iconName === "fill" &&
		typeof task.progressTotal === "number" &&
		task.progressTotal > 0;
	const pct = typeof task.progress === "number" ? task.progress : 0;
	const completedCount = showProgress
		? Math.round((pct / 100) * (task.progressTotal ?? 0))
		: 0;
	const progressUnit = task.progressUnit ?? "items";

	return (
		<article
			className={styles.card}
			data-category={task.category}
			data-view={view}
		>
			<div className={styles.topfill}>
				<span className={styles.deadline}>{task.deadline}</span>
			</div>

			<div className={styles.headlineRow}>
				<ActionIcon
					className={styles.icon}
					strokeWidth={1.5}
					aria-hidden="true"
				/>
				<h3 className={styles.title}>{task.title}</h3>
			</div>

			<p className={styles.description}>{task.description}</p>

			<div className={styles.footer}>
				{showProgress ? (
					<div className={styles.progressBlock}>
						<div className={styles.progressLabelRow}>
							<span className={styles.progressLabel}>
								{progressLabel
									? progressLabel(
											completedCount,
											task.progressTotal ?? 0,
											progressUnit,
										)
									: `${completedCount} of ${task.progressTotal} ${progressUnit} completed`}
							</span>
							<span className={styles.progressPct}>{pct}%</span>
						</div>
						<div
							className={styles.progressTrack}
							role="progressbar"
							aria-valuenow={pct}
							aria-valuemin={0}
							aria-valuemax={100}
						>
							<div
								className={styles.progressFill}
								style={
									{
										"--progress-pct": `${pct}%`,
									} as CSSProperties
								}
							/>
						</div>
					</div>
				) : null}

				<div className={styles.bottomRow}>
					<div className={styles.bottomLeft}>
						<button
							type="button"
							className={styles.moreButton}
							aria-label={`More options for ${task.title}`}
							disabled
							aria-disabled="true"
						>
							<EllipsisVerticalIcon width={20} height={20} aria-hidden="true" />
						</button>

						<button
							type="button"
							className={styles.askEveButton}
							onClick={() => onAskEve(task)}
							aria-label={askEveLabel}
						>
							<img
								src="/images/education/icon-ask-eve.svg"
								alt=""
								className={styles.askEveIcon}
								aria-hidden="true"
							/>
							<span>{askEveLabel}</span>
						</button>
					</div>

					<button
						type="button"
						className={styles.chevronButton}
						onClick={() => onChevronClick(task)}
						aria-label={`Open ${task.title}`}
					>
						<ChevronRightIcon
							className={styles.chevronIcon}
							aria-hidden="true"
						/>
					</button>
				</div>
			</div>
		</article>
	);
}
